import { createContext, useContext, useEffect, useState, useCallback } from "react";
import * as API from "../lib/api";
import { ensurePushSubscribed } from "../lib/push";

const THEME_KEY = "journey:theme";

function readCachedTheme() {
  try {
    const t = localStorage.getItem(THEME_KEY);
    return t === "light" || t === "dark" ? t : "dark";
  } catch {
    return "dark";
  }
}

// Apply cached theme synchronously on module import to eliminate flash
if (typeof document !== "undefined") {
  const t = readCachedTheme();
  document.documentElement.classList.remove("light", "dark");
  document.documentElement.classList.add(t);
}

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [settings, setSettings] = useState({
    theme: readCachedTheme(),
    notifications_enabled: false,
    user_email: "",
    user_name: "",
  });
  const [loaded, setLoaded] = useState(false);

  const refreshConversations = useCallback(async () => {
    try {
      const list = await API.listConversations();
      setConversations(list);
      return list;
    } catch (e) {
      console.error("refreshConversations", e);
      return [];
    }
  }, []);

  const refreshSettings = useCallback(async () => {
    try {
      const s = await API.getSettings();
      setSettings((prev) => ({ ...prev, ...s }));
      return s;
    } catch (e) {
      console.error("refreshSettings", e);
      return null;
    }
  }, []);

  const saveSettings = useCallback(async (patch) => {
    try {
      const s = await API.updateSettings(patch);
      setSettings((prev) => ({ ...prev, ...s }));
      return s;
    } catch (e) {
      console.error("saveSettings", e);
      throw e;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([refreshConversations(), refreshSettings()]);
      setLoaded(true);
    })();
  }, [refreshConversations, refreshSettings]);

  // V9.37 — Push subscription reconciliation.
  //
  // After settings load, if the user previously enabled notifications
  // AND the OS-level permission is still granted, idempotently ensure
  // we have an active push subscription registered with the backend.
  // This rescues the dead-sub state that follows a force-refresh /
  // PWA reinstall: the OS permission survives, but the SW
  // registration that owned the subscription was unregistered, so the
  // sub silently disappeared. Without this, closed-app reminders stop
  // firing forever — the user has no way to recover short of toggling
  // notifications off + on in Settings.
  //
  // We also re-check on `visibilitychange` so a long-running tab that
  // had its sub invalidated while backgrounded heals on next foreground.
  // `ensurePushSubscribed` is idempotent (compares VAPID key + reuses
  // existing sub) so frequent calls cost nothing.
  useEffect(() => {
    if (!loaded) return;
    const wantsPush =
      settings?.notifications_enabled === true &&
      typeof Notification !== "undefined" &&
      Notification.permission === "granted";
    if (!wantsPush) return;
    ensurePushSubscribed().catch(() => {});
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        ensurePushSubscribed().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [loaded, settings?.notifications_enabled]);

  // Apply theme class to document root + cache to localStorage
  useEffect(() => {
    const root = document.documentElement;
    const theme = settings.theme === "light" ? "light" : "dark";
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {}
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "light" ? "#f7f5f1" : "#050505");
  }, [settings.theme]);

  return (
    <AppContext.Provider
      value={{
        conversations,
        setConversations,
        activeConvId,
        setActiveConvId,
        settings,
        saveSettings,
        refreshConversations,
        refreshSettings,
        loaded,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
